import { Injectable, inject } from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '@environments/environment';

export interface SsoDiscoverResult {
  organizationId: number | null;
  ssoEnabled: boolean;
  protocol: 'oidc' | 'saml' | null;
}

export interface SsoProvider {
  key: string;
  label: string;
  logo: string;
  defaults?: Record<string, string>;
}

export interface SsoGuideAction {
  type: 'copy' | 'link';
  label: string;
  valueKey?: string;
  value?: string;
  url?: string;
}

export interface SsoGuideCopyValue {
  key: string;
  label: string;
  value: string;
}

export interface SsoProviderGuide {
  label: string;
  markdown: string;
  actions?: SsoGuideAction[];
  copyValues?: SsoGuideCopyValue[];
}

export interface OrganizationSsoConfig {
  enabled: boolean;
  protocol: 'oidc' | 'saml' | null;
  provider: string;
  config_json: Record<string, unknown>;
  role_mapping_enabled: boolean;
  entity_mapping_enabled: boolean;
  allow_password_fallback: boolean;
  do_not_lock_when_disabled: boolean;
  auto_provision_users: boolean;
  sso_domains?: string[];
  provision_domains?: string[];
  empty_claim_revokes_access: boolean;
  role_claim: string;
  role_map_json: Record<string, string>;
  entity_mapping_strategy: 'platform_unique_id' | 'snoball_id_claim_name';
  client_claim: string;
  project_claim: string;
  callback_url?: string;
  saml_callback_url?: string;
  saml_logout_url?: string;
  invalidation_url?: string;
  session_invalidation_token_set?: boolean;
  session_invalidation_token_plain?: string;
}

@Injectable({providedIn: 'root'})
export class OrganizationSsoService {
  private http = inject(HttpClient);

  private baseApiUrl = environment.apiUrl;

  discover(email: string): Observable<{ data: SsoDiscoverResult }> {
    return this.http.get<{ data: SsoDiscoverResult }>(
      `${this.baseApiUrl}/api/v1/sso/discover`,
      {
        params: {email},
        headers: new HttpHeaders().set('Skip-auth', 'true')
      }
    );
  }

  getConfig(organizationId: number): Observable<{ data: OrganizationSsoConfig | null }> {
    return this.http.get<{ data: OrganizationSsoConfig }>(
      `${this.baseApiUrl}/api/v1/organization/${organizationId}/sso`
    ).pipe(
      map((resp) => ({ data: resp.data || null }))
    );
  }

  updateConfig(
    organizationId: number,
    payload: Partial<OrganizationSsoConfig>
  ): Observable<{ data: OrganizationSsoConfig | null; message?: string }> {
    return this.http.put<{ data: OrganizationSsoConfig; message?: string }>(
      `${this.baseApiUrl}/api/v1/organization/${organizationId}/sso`,
      payload
    ).pipe(
      map((resp) => ({
        data: resp.data || null,
        message: resp.message
      }))
    );
  }

  testConnection(organizationId: number): Observable<{ data: { success: boolean; message: string } }> {
    return this.http.post<{ data: { success: boolean; message: string } }>(
      `${this.baseApiUrl}/api/v1/organization/${organizationId}/sso/test`,
      {}
    );
  }

  regenerateInvalidationToken(organizationId: number): Observable<{ data: OrganizationSsoConfig | null }> {
    return this.http.post<{ data: OrganizationSsoConfig }>(
      `${this.baseApiUrl}/api/v1/organization/${organizationId}/sso/regenerate-invalidation-token`,
      {}
    ).pipe(
      map((resp) => ({ data: resp.data || null }))
    );
  }

  listProviders(protocol: 'oidc' | 'saml'): Observable<{ data: SsoProvider[] }> {
    return this.http.get<{ data: SsoProvider[] }>(
      `${this.baseApiUrl}/api/v1/sso/providers`,
      {params: {protocol}}
    );
  }

  getProviderGuide(
    protocol: 'oidc' | 'saml',
    providerKey: string,
    organizationId?: number
  ): Observable<{ data: SsoProviderGuide }> {
    const params: Record<string, string> = {};
    if (organizationId) {
      params.organizationId = String(organizationId);
    }
    return this.http.get<{ data: SsoProviderGuide }>(
      `${this.baseApiUrl}/api/v1/sso/providers/${protocol}/${providerKey}/guide`,
      {params}
    );
  }

  getDerivedUrls(organizationId: number): Pick<OrganizationSsoConfig, 'callback_url' | 'saml_callback_url' | 'invalidation_url'> {
    const base = this.getPublicBaseUrl();
    return {
      callback_url: `${base}/b/sso/${organizationId}/callback`,
      saml_callback_url: `${base}/b/sso/${organizationId}/saml/callback`,
      invalidation_url: `${base}/b/sso/${organizationId}/invalidate-sessions`
    };
  }

  private getPublicBaseUrl(): string {
    return (environment.frontUrl || environment.apiUrl).replace(/#.*$/, '').replace(/\/$/, '');
  }

  /** `remember` is the sign-in page's "Remember me"; the server keeps it for the callback. */
  getLoginUrl(organizationId: number, remember = false): string {
    return `${this.baseApiUrl}/api/v1/sso/${organizationId}/login${remember ? '?remember=1' : ''}`;
  }

  getVerifiedOrganizationDomains(organizationId: number): Observable<{ data: Array<{ identity: string }> }> {
    return this.http.get<{ data: Array<{ identity: string }> }>(
      `${this.baseApiUrl}/api/v1/mailer-domain/organization/${organizationId}/verified`
    );
  }
}
